/**
 * This file should export two objects, userFeatures and anonymousFeatures
 * userFeatures are features that will receive the user id where as
 * anonymousFeatures will receive the anonymous id.
 *
 * This separation is needed in split to ensure tracking is consistent for a user
 * from the home page all the way to checkout.
 *
 * All new features and feature updates should be documented here:
 * https://farmersdog.slab.com/posts/split-io-features-bxahk2oq
 */

import {
  AcAltGetStartedPageTreatments,
  CvrTreatsInCheckoutUiTreatments,
} from '@farmersdog/tosa/src/utils';
import {
  AcMe3PageTreatments,
  CustomerClinicSearchTreatments,
  CvrSignupCardSelectorsTreatments,
  CvrSmsOptInTreatments,
  CvrSurpriseHigherDiscountTreatments,
  DefaultDiscountTreatments,
  HomepageEndToEndRedesignTreatments,
  HomepageHeroImageTreatments,
  ReferralsGiftABoxVariant,
  ReviewsPageRedesignTreatments,
} from './types';

import { nameToken } from '@farmersdog/tosa/src/components/shared/SignupHeader/DiscountBanner';

/**
 * Meta data around a feature used for various purposes throughout the website.
 */
interface FeatureMetaData {
  /** All expected treatment values */
  treatments: string[];
  /** All expected treatment values */
  track: boolean;
  /** Treatment data to use in development */
  mock: SplitIO.TreatmentWithConfig;
}

/**
 * Object with feature meta data. Specify the featureName as key and the meta data as value.
 */
interface Features {
  [featureName: string]: FeatureMetaData;
}

/**
 * Feature names
 */

export const CVR_MIXING_PLAN_EXPERIMENT = 'cvr_mixing_plan_experiment';
export const CVR_POST_CHECKOUT_REFERRAL_PERCENTAGE =
  'cvr_post_checkout_referral_percentage';
export const CX_ALERT_BANNER = 'cx_alert_banner';
export const TSX_SNAP_STICKS_SOLD_OUT = 'TSX_SNAP_STICKS_SOLD_OUT';
export const BEEF_MLENTILS_ENABLED = 'beef_mlentils_enabled';
export const BEEF_MLENTILS_UI_ROLLBACK_ENABLED =
  'beef_mlentils_ui_rollback_enabled';
export const CVR_DEFAULT_DISCOUNT = 'cvr_default_discount';
export const CVR_DISABLE_REFERRER_LOOKUP = 'cvr_disable_referrer_lookup';
export const CVR_GLOBALDISCOUNT_OVERRIDE = 'cvr_globaldiscount_override';
export const CVR_SIGNUP_FLOW_DOWN = 'cvr_signup_flow_down';
export const AC_HOMEPAGE_END_TO_END_REDESIGN =
  'ac_homepage_end_to_end_redesign';
export const AC_ALT_GET_STARTED_PAGE = 'ac_alt_get_started_page';
export const AC_REVIEWS_PAGE_REDESIGN = 'ac_reviews_page_redesign';
export const AC_GLOBAL_FOOTER_REDESIGN = 'ac_global_footer_redesign';
export const CVR_TOSA_HIGH_TRAFFIC_MODE = 'cvr_tosa_high_traffic_mode';
export const CVR_HOMEPAGE_VIDEO_HERO = 'cvr_homepage_video_hero';
export const CVR_CTA_ACCESSIBILITY_HOMEPAGE = 'cvr_cta_accessibility_homepage';
export const CVR_HEALTH_ISSUE_URINARY_STONES =
  'cvr_health_issue_urinary_stones';
export const CVR_SMS_OPTIN_COPY = 'cvr_sms_optin_copy';
export const CVR_LINK_HOMEPAGE_HEADLINE = 'cvr_link_homepage_headline';
export const CVR_MOVE_NAV_LOGO_DOWN = 'cvr_move_nav_logo_down';
export const CVR_SURPRISE_HIGHER_DISCOUNT = 'cvr_surprise_higher_discount';
export const CVR_TREATS_IN_CHECKOUT_UI = 'cvr_treats_in_checkout_ui';
export const CVR_HOMEPAGE_TERMS_CHANGES_BANNER =
  'cvr_homepage_terms_changes_banner';
export const CVR_ANONYMOUS_ID_PERFORMANCE_LOGS =
  'cvr_anonymous_id_performance_logs';
export const CVR_SIGNUP_ICON_UPDATE = 'cvr_signup_icon_update';
export const CVR_HOMEPAGE_HERO_IMAGE = 'cvr_homepage_hero_image';
export const CVR_TOSA_DISCOUNT_BANNER_COPY = 'cvr_tosa_discount_banner_copy';
export const CVR_HOMEPAGE_EXIT_INTENT = 'cvr_homepage_exit_intent';
export const CVR_CHECKOUT_TERMS_PRIVACY_CHECKBOX =
  'cvr_checkout_terms_privacy_checkbox';
export const CVR_SIGNUP_CARD_SELECTORS = 'cvr_signup_card_selectors';
export const CVR_SIGNUP_DEBUG_REDIRECT = 'cvr_signup_debug_redirect';
export const CVR_SURPRISE_FREE_TREATS_BANNER =
  'cvr_surprise_free_treats_banner';
export const TERMS_CHANGES_BANNER = 'terms_changes_banner';
export const TSX_GLADLY_LIVE_CHAT = 'tsx_gladly_live_chat';
export const TSX_TREATS_PDP_PRICE_TEST = 'tsx_treats_pdp_price_test_enabled';
export const NARROW_DELIVERY_WINDOW = 'narrow_delivery_window';
export const TSX_SNAP_STICKS_HIDE = 'tsx_snap_sticks_hide';
export const TSX_TREATS_RECOMMEND_1_BAG = 'tsx_treats_recommend_1_bag';
export const ONB_CHECKOUT_FREQUENCY_CAP = 'onb_checkout_frequency_cap';
export const INT_OPS_MEO_ENABLE_GENERATE_LABEL =
  'int_ops_meo_enable_generate_label';
export const SSP_SCHEDULE_CARD = 'ssp_schedule_card';
export const SSP_CALORIE_CHANGE_WEEKLY_PRICE_TO_DAILY_PRICE =
  'ssp_calorie_change_weekly_price_to_daily_price';
export const SSP_TREATS_LARGE_BAG_DISCOUNT = 'ssp_treats_large_bag_discount';
export const SSP_CALIBRATION_FLOW_DETAILED_PRICES =
  'ssp_calibration_flow_detailed_prices';
export const SSP_CALIBRATION_OPTION_FIX = 'ssp_calibration_option_fix';
export const SSP_FREE_FIRST_TREAT_ENABLED = 'ssp_free_first_treat_enabled';
export const SSP_SNAKE_GAME_ENABLED = 'ssp_snake_game_enabled';
export const SSP_SUBSCRIBE_AND_SAVE_CHECKOUT_FE =
  'ssp_subscribe_and_save_checkout_fe';
export const ADJ_REDIRECT_ADJUST_CALORIES_TO_CX =
  'adj_redirect_adjust_calories_to_cx';
const HEALTH_CUSTOMER_CLINIC_SEARCH = 'HEALTH_CUSTOMER_CLINIC_SEARCH';
export const ADJ_ADD_DOG_FORM = 'adj_add_dog_form';
export const ADJ_CX_CHAT_AB_TESTING = 'adj_cx_chat_ab_testing';
export const ADJ_BILLING_TIMELINE_SERVICE_ENABLE =
  'adj_billing_timeline_service_enable';
export const ADJ_MULTIPLE_PAYMENT_METHODS = 'adj_multiple_payment_methods';
export const ADJ_EDIT_PET_UI_IMPROVEMENTS = 'adj_edit_pet_ui_improvements';
export const ADJ_DIY_REACTIVATION_CGS_ENABLE =
  'adj_diy_reactivation_cgs_enable';
export const ADJ_BILLING_RESCHEDULE_REASON = 'adj_billing_reschedule_reason';
export const ADJ_EDIT_PET_LOWEST_CALORIE_BUCKET =
  'adj_edit_pet_lowest_calorie_bucket';
export const ADJ_EDIT_PET_UPDATE_CONTACT_CX_MODAL =
  'adj_edit_pet_update_contact_cx_modal';
export const ADJ_MULTIDOG_SUBSCRIPTION_LINK_COPY =
  'adj_multidog_subscription_link_copy';
export const ADJ_REMOVE_MANAGE_BUTTON_FOR_FAILED_ORDERS =
  'adj_remove_manage_button_for_failed_orders';
export const ADJ_TMF_RESCHEDULE_UPDATE_PLAN = 'adj_tmf_reschedule_update_plan';
export const ADJ_TMF_NO_INVENTORY = 'adj_tmf_no_inventory';
export const ADJ_CUSTOM_MAX_PACKS = 'adj_custom_max_packs';
export const ADJ_STRIPE_CARD_ERROR_UPDATE = 'adj_stripe_card_error_update';
export const ADJ_REACTIVATION_PAYMENT_FAILED_WARNING =
  'adj_reactivation_payment_failed_warning';
export const ADJ_UPDATE_MAX_SAFETY_STOCK = 'adj_update_max_safety_stock';
export const ADJ_ACCOUNT_DASHBOARD = 'adj_account_dashboard';
export const ADJ_TEMPORARY_FEEDING_PORTION_EXPERIMENT =
  'adj_temporary_feeding_portion_experiment';
export const ADJ_CALIBRATION_CALORIES_PACKS_SWITCH =
  'adj_calibration_calories_packs_switch';
export const ACQ_REFERRALS_GIFT_A_BOX = 'acq_referrals_gift_a_box';
export const AC_REFERRALS_PERSIST_CODE = 'ac_referrals_persist_code';
export const FRONTEND_LOGGING_ENABLED = 'frontend_logging_enabled';
export const ONB_LPF_DOWNSELL_FOR_MULTIPET = 'onb_lpf_downsell_for_multipet';
export const ONB_USE_TRIAL_ORDER_DAILY_PRICE_IN_CHECKOUT =
  'onb_use_trial_order_daily_price_in_checkout';
export const AC_ME3_PAGE = 'ac_me3_page';

// ProductLaunch experiments
export const PLX_CHEWS_ENABLED = 'chews_product_launch';

/**
 * Collection of expected split features that should be called with a user id
 * from segment.
 */
export const userFeatures: Features = {
  customer_cx_phone_number: {
    treatments: ['on', 'off'],
    track: false,
    mock: {
      treatment: 'on',
      config: JSON.stringify({
        footer: {
          email: true,
          phone: true,
        },
        faq: {
          email: true,
          phone: true,
        },
        diySupport: {
          phone: true,
          email: true,
        },
      }),
    },
  },
  show_holiday_announcement: {
    treatments: ['off', 'on'],
    track: false,
    mock: {
      treatment: 'off',
      config: JSON.stringify({
        headline: 'Order Scheduled for Holiday Week',
        underline:
          "We noticed your next order is scheduled during a holiday week. If you'll be traveling, check out our ${faq} to make sure you know how to change your address or ${reschedule} your order before ${rescheduleCutoff}.",
        endDate: '2020-12-22',
        cutoffStart: '2020-11-23',
        cutoffEnd: '2021-01-02',
      }),
    },
  },
  express_pay_signup: {
    track: false,
    treatments: ['on', 'off'],
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  frequencies_at_checkout: {
    treatments: ['trans_box', '28_cap', 'control'],
    track: true,
    mock: {
      treatment: 'trans_box',
      config: null,
    },
  },
  chicken_subscriber_web: {
    treatments: ['on', 'off'],
    track: false,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  chicken_funnel_v1: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  cancel_inflight_warning: {
    treatments: ['on', 'off'],
    track: false,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  recipe_names: {
    treatments: ['off', 'vegetables', 'ingredients'],
    track: true,
    mock: {
      treatment: 'ingredients',
      config: JSON.stringify({
        TURKEY: 'parsnip',
        BEEF: 'lentil',
        PORK: 'cauliflower',
        CHICKEN_AND_GREENS: 'brussel sprouts',
      }),
    },
  },
  ret_holidayfaqs_toggle: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  cvr_quicker_trials: {
    treatments: ['off', 'on'],
    track: false,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  cvr_signup_debug_redirect: {
    treatments: ['off', 'on'],
    track: false,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [TSX_SNAP_STICKS_SOLD_OUT]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'off',
      config: JSON.stringify({}),
    },
  },
  [CVR_MIXING_PLAN_EXPERIMENT]: {
    treatments: [
      'off',
      'control',
      'treatmentControl',
      'treatmentA',
      'treatmentB',
      'treatmentC',
    ],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_SIGNUP_CARD_SELECTORS]: {
    treatments: Object.values(CvrSignupCardSelectorsTreatments),
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_POST_CHECKOUT_REFERRAL_PERCENTAGE]: {
    treatments: [
      'off',
      'control',
      '20_percent_off',
      '60_percent_off',
      '100_percent_off',
    ],
    track: true,
    mock: {
      treatment: 'off',
      config: JSON.stringify({
        couponCode: 'referral60',
      }),
    },
  },
  [BEEF_MLENTILS_ENABLED]: {
    treatments: ['on', 'off'],
    track: false,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [BEEF_MLENTILS_UI_ROLLBACK_ENABLED]: {
    treatments: ['on', 'off'],
    track: false,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_HEALTH_ISSUE_URINARY_STONES]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_SMS_OPTIN_COPY]: {
    treatments: Object.values(CvrSmsOptInTreatments),
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_SURPRISE_HIGHER_DISCOUNT]: {
    treatments: Object.values(CvrSurpriseHigherDiscountTreatments),
    track: true,
    mock: {
      treatment: 'off',
      config: JSON.stringify({
        code: 'surprisediscount100v2hmjku',
      }),
    },
  },
  [CVR_SURPRISE_FREE_TREATS_BANNER]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_TREATS_IN_CHECKOUT_UI]: {
    treatments: Object.values(CvrTreatsInCheckoutUiTreatments),
    track: true,
    mock: {
      treatment: 'page',
      config: null,
    },
  },
  [TSX_GLADLY_LIVE_CHAT]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [TSX_TREATS_PDP_PRICE_TEST]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [SSP_TREATS_LARGE_BAG_DISCOUNT]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [TSX_SNAP_STICKS_HIDE]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'off',
      config: JSON.stringify({}),
    },
  },
  [TSX_TREATS_RECOMMEND_1_BAG]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [SSP_SCHEDULE_CARD]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [SSP_CALORIE_CHANGE_WEEKLY_PRICE_TO_DAILY_PRICE]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [SSP_CALIBRATION_FLOW_DETAILED_PRICES]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [SSP_CALIBRATION_OPTION_FIX]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [SSP_SNAKE_GAME_ENABLED]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [SSP_SUBSCRIBE_AND_SAVE_CHECKOUT_FE]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [ADJ_REDIRECT_ADJUST_CALORIES_TO_CX]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [HEALTH_CUSTOMER_CLINIC_SEARCH]: {
    treatments: Object.values(CustomerClinicSearchTreatments),
    track: true,
    mock: {
      treatment: CustomerClinicSearchTreatments.on,
      config: null,
    },
  },
  [ADJ_ADD_DOG_FORM]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [ADJ_CX_CHAT_AB_TESTING]: {
    treatments: ['default', 'sidekickdemo'],
    track: true,
    mock: {
      treatment: 'default',
      config: JSON.stringify({
        appId: 'thefarmersdog.com',
      }),
    },
  },
  [ADJ_EDIT_PET_UI_IMPROVEMENTS]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [ADJ_BILLING_RESCHEDULE_REASON]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  [ADJ_EDIT_PET_LOWEST_CALORIE_BUCKET]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  [ADJ_EDIT_PET_UPDATE_CONTACT_CX_MODAL]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  [ADJ_MULTIDOG_SUBSCRIPTION_LINK_COPY]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  [SSP_FREE_FIRST_TREAT_ENABLED]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [ADJ_TMF_RESCHEDULE_UPDATE_PLAN]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  [ADJ_TMF_NO_INVENTORY]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  [ADJ_CUSTOM_MAX_PACKS]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  [ADJ_REACTIVATION_PAYMENT_FAILED_WARNING]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  [ACQ_REFERRALS_GIFT_A_BOX]: {
    treatments: Object.values(ReferralsGiftABoxVariant),
    track: true,
    mock: {
      treatment: ReferralsGiftABoxVariant.off,
      config: null,
    },
  },
  [PLX_CHEWS_ENABLED]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  [ONB_LPF_DOWNSELL_FOR_MULTIPET]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [ONB_USE_TRIAL_ORDER_DAILY_PRICE_IN_CHECKOUT]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_CHECKOUT_TERMS_PRIVACY_CHECKBOX]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [ADJ_ACCOUNT_DASHBOARD]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  [ADJ_TEMPORARY_FEEDING_PORTION_EXPERIMENT]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  [ADJ_CALIBRATION_CALORIES_PACKS_SWITCH]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  [FRONTEND_LOGGING_ENABLED]: {
    treatments: ['on', 'off'],
    track: true,
    mock: { treatment: 'off', config: null },
  },
};

/**
 * Collection of expected split features that should be called with an anonymous
 * id from segment.
 */
export const anonymousFeatures: Features = {
  cvr_citytakeover_redirects: {
    treatments: ['on', 'off'],
    track: false,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  cvr_condensed_press_bar: {
    treatments: ['on', 'off'],
    track: false,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  prospective_cx_phone_number: {
    treatments: ['on', 'off'],
    track: false,
    mock: {
      treatment: 'on',
      config: JSON.stringify({
        footer: {
          phone: true,
          email: true,
        },
        diySupport: {
          phone: false,
          email: true,
        },
      }),
    },
  },
  [CVR_GLOBALDISCOUNT_OVERRIDE]: {
    treatments: ['on', 'off'],
    track: false,
    mock: {
      treatment: 'off',
      config: `{ "discountPercentage": 100, "bannerCopy": "Surprise! We're offering a 100% off just today!" }`,
    },
  },
  [CX_ALERT_BANNER]: {
    treatments: ['on', 'off'],
    track: false,
    mock: {
      treatment: 'off',
      config: JSON.stringify({
        bannerCopy: 'We are experiencing high volume etc etc',
        link: '/faq',
      }),
    },
  },
  [CVR_TOSA_HIGH_TRAFFIC_MODE]: {
    treatments: ['off', 'on'],
    track: false,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_ANONYMOUS_ID_PERFORMANCE_LOGS]: {
    treatments: ['off', 'on'],
    track: false,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_DEFAULT_DISCOUNT]: {
    treatments: Object.values(DefaultDiscountTreatments),
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_SIGNUP_FLOW_DOWN]: {
    treatments: ['off', 'on'],
    track: false,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [AC_HOMEPAGE_END_TO_END_REDESIGN]: {
    treatments: Object.values(HomepageEndToEndRedesignTreatments),
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [AC_REVIEWS_PAGE_REDESIGN]: {
    treatments: Object.values(ReviewsPageRedesignTreatments),
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [AC_ALT_GET_STARTED_PAGE]: {
    treatments: Object.values(AcAltGetStartedPageTreatments),
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [AC_REFERRALS_PERSIST_CODE]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  [AC_GLOBAL_FOOTER_REDESIGN]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_DISABLE_REFERRER_LOOKUP]: {
    treatments: ['off', 'on'],
    track: false,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_CTA_ACCESSIBILITY_HOMEPAGE]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_LINK_HOMEPAGE_HEADLINE]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_MOVE_NAV_LOGO_DOWN]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_HOMEPAGE_TERMS_CHANGES_BANNER]: {
    treatments: ['off', 'on'],
    track: false,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_SIGNUP_ICON_UPDATE]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [CVR_HOMEPAGE_HERO_IMAGE]: {
    treatments: Object.values(HomepageHeroImageTreatments),
    track: true,
    mock: {
      treatment: HomepageHeroImageTreatments.Off,
      config: null,
    },
  },
  [CVR_TOSA_DISCOUNT_BANNER_COPY]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: JSON.stringify({
        bannerCopy: `${nameToken} has gifted you 100% off your first box! 🎉`,
        bannerLinkCopy: 'See Offer Terms',
        bannerLinkPath: '/content/offerterms',
      }),
    },
  },
  [CVR_HOMEPAGE_EXIT_INTENT]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [INT_OPS_MEO_ENABLE_GENERATE_LABEL]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [ADJ_BILLING_TIMELINE_SERVICE_ENABLE]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  [ADJ_MULTIPLE_PAYMENT_METHODS]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [TERMS_CHANGES_BANNER]: {
    treatments: ['off', 'on'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [ADJ_REMOVE_MANAGE_BUTTON_FOR_FAILED_ORDERS]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  [ADJ_STRIPE_CARD_ERROR_UPDATE]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'on',
      config: null,
    },
  },
  [ADJ_UPDATE_MAX_SAFETY_STOCK]: {
    treatments: ['on', 'off'],
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
  [AC_ME3_PAGE]: {
    treatments: Object.values(AcMe3PageTreatments),
    track: true,
    mock: {
      treatment: 'off',
      config: null,
    },
  },
};

/**
 * Convert a feature config into an object that can be
 * used by the split factory for local testing.
 *
 * @param features - Array of features to get mock treatment data from
 * @returns
 */
function getMockedFeatureMap(features: Features): SplitIO.MockedFeaturesMap {
  const asLocal: SplitIO.MockedFeaturesMap = {};

  Object.entries(features).forEach(([key, feature]) => {
    asLocal[key] = feature.mock;
  });

  return asLocal;
}

/**
 * Converted features for local testing
 */
export const mockedFeatures: SplitIO.MockedFeaturesMap = {
  ...getMockedFeatureMap(userFeatures),
  ...getMockedFeatureMap(anonymousFeatures),
};
